import { motion } from 'framer-motion';
import { Polaroid } from '../components/ui/Polaroid';
import { SiteFooter } from './AboutPage';
import { MarketingNav, BackToTop } from '../components/ui/MarketingNav';
import { InkUnderline } from '../components/ui/RevealText';

const PILLARS = [
  {
    n: 'I',
    title: 'Simplitate înainte de orice.',
    body:
      'Fiecare decizie de design trece prin același test: poate unchiul Gheorghe, la 67 de ani, să facă asta fără să întrebe pe nimeni? Dacă nu, simplificăm până când dă. Fără „dar e doar un click în plus". Fără onboarding. Fără tutorial. Fără explicații.',
  },
  {
    n: 'II',
    title: 'Pozele aparțin oamenilor, nu platformei.',
    body:
      'Noi nu ne antrenăm AI pe pozele nunții tale. Nu le vindem, nu le folosim în marketing, nu le stocăm pe servere din SUA. Galeria ta se șterge când ne spui tu. Punct.',
  },
  {
    n: 'III',
    title: 'Frumusețea e o funcționalitate, nu un decor.',
    body:
      'O poză frumos pusă în pagină se uită mai mult decât una pe un grid de 12 coloane. De-asta avem Modul Revistă. De-asta tipografia contează mai mult decât viteza de încărcare. Vrem să stai, nu să dai scroll.',
  },
  {
    n: 'IV',
    title: 'Facem puțin, dar bine.',
    body:
      'Nu vom adăuga livestream, nu vom adăuga chatbot pentru „vorbire cu mirii", nu vom adăuga funcții pe care ni le cere o singură nuntă pe an. Dacă ceva nu servește 95% din evenimente, nu intră în produs.',
  },
];

export default function MissionPage() {
  return (
    <div className="min-h-screen">
      <MarketingNav />

      {/* ─── Hero / Manifesto opener ─── */}
      <section className="container-page relative pb-3xl pt-lg">
        <div className="mx-auto max-w-4xl text-center">
          <p className="kicker justify-center">Misiunea noastră</p>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mt-md font-serif"
            style={{
              fontSize: 'clamp(2.6rem, 6vw, 5rem)',
              lineHeight: 1.02,
              fontVariationSettings: "'opsz' 144, 'SOFT' 100, 'WONK' 1",
            }}
          >
            Să facem din fiecare telefon
            <br />
            <em className="text-gradient italic">o parte din poveste.</em>
          </motion.h1>

          <div className="squiggle mx-auto mt-lg" />

          <p className="mt-lg text-lg text-fg-soft">
            GuestLens nu e o aplicație, nu e un SaaS, nu e un startup care
            promite „revoluționăm modul în care-ți păstrezi amintirile".
            E un loc mic, făcut cu grijă, pentru un singur lucru:{' '}
            <InkUnderline>transformarea zilei tale într-o carte pe care
            s-o răsfoiești</InkUnderline>.
          </p>
        </div>

        {/* Hero polaroid */}
        <div className="relative mx-auto mt-3xl max-w-md">
          <Polaroid
            src="https://images.unsplash.com/photo-1469371670807-013ccf25f16a?w=900&q=80"
            alt="Pagina de copertă a unui album"
            caption="✦ ediția I"
            rotate={-2}
            aspect="3/4"
          />
        </div>
      </section>

      {/* ─── The long form: pillars I-IV ─── */}
      <section className="bg-bg-alt py-4xl">
        <div className="container-page">
          <div className="mx-auto max-w-3xl">
            <p className="kicker">Patru piloni</p>
            <h2 className="mt-sm" style={{ fontVariationSettings: "'opsz' 144" }}>
              Lucruri pe care nu le negociem.
            </h2>
            <p className="mt-md max-w-xl text-fg-soft">
              Ori de câte ori trebuie să alegem între o facilitate și un
              principiu, principiul câștigă. Am scris astea pe perete
              anul trecut și n-am șters încă nimic.
            </p>
          </div>

          <ol className="mt-2xl space-y-3xl">
            {PILLARS.map((p) => (
              <motion.li
                key={p.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.8 }}
                className="grid grid-cols-1 gap-xl lg:grid-cols-12"
              >
                <div className="lg:col-span-3">
                  <p
                    className="font-serif text-fg-soft"
                    style={{
                      fontSize: 'clamp(5rem, 9vw, 8rem)',
                      lineHeight: 0.85,
                      fontVariationSettings: "'opsz' 144",
                    }}
                  >
                    {p.n}
                  </p>
                </div>
                <div className="lg:col-span-9">
                  <h3
                    className="font-serif"
                    style={{
                      fontSize: 'clamp(1.8rem, 3vw, 2.6rem)',
                      lineHeight: 1.1,
                      fontVariationSettings: "'opsz' 144",
                    }}
                  >
                    {p.title}
                  </h3>
                  <p className="mt-md max-w-2xl text-lg text-fg-soft">
                    {p.body}
                  </p>
                </div>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      {/* ─── What we refuse ─── */}
      <section className="container-page py-4xl">
        <div className="grid grid-cols-1 gap-2xl lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="kicker">Ce refuzăm</p>
            <h2 className="mt-sm" style={{ fontVariationSettings: "'opsz' 144" }}>
              Și lucruri pe care le-am refuzat.
            </h2>
            <p className="mt-md text-fg-soft">
              „Nu" e deseori cel mai important lucru pe care-l poate spune
              o echipă mică. Ne ajută să rămânem la fel de clare ca în
              prima zi.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="divide-y divide-border">
              {[
                ['Reclame în galerie.', 'Nu vor exista. Niciun banner, niciun „vă recomandăm", niciun link sponsorizat între pozele mirilor. N-am construit chestia asta ca s-o stricăm.'],
                ['Vânzarea datelor.', 'Pozele nunții tale nu ajung într-un set de date. Nu antrenăm nimic pe ele. Nici măcar pentru „îmbunătățirea experienței".'],
                ['Funcții făcute pentru pitch.', 'Dacă o facilitate arată bine într-o prezentare dar n-o folosește nimeni, n-o facem. Promoțiile de tip „AI-ul alege cele mai frumoase cadre" — asta e treaba ta, nu a noastră.'],
                ['Creștere cu orice preț.', 'Nu vrem 100.000 de nunți. Vrem 1.000 de nunți la care oamenii chiar s-au bucurat de galerie.'],
              ].map(([title, body]) => (
                <li key={title} className="py-md">
                  <div className="flex items-baseline gap-md">
                    <span className="font-mono text-accent">✕</span>
                    <p className="font-serif text-xl">{title}</p>
                  </div>
                  <p className="mt-xs pl-md text-fg-soft">{body}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ─── The promise ─── */}
      <section className="border-y border-border bg-bg-alt py-3xl">
        <div className="container-page">
          <div className="mx-auto max-w-3xl text-center">
            <p className="kicker justify-center">Promisiunea</p>
            <h2 className="mt-sm" style={{ fontVariationSettings: "'opsz' 144" }}>
              Dacă ne dai o seară din viața ta,
              <br />
              <em className="italic text-fg-soft">îți dăm o carte pe viață</em>.
            </h2>
            <p className="mt-lg text-fg-soft">
              Cam atât. Fără engagement metrics, fără time-on-page, fără
              notificări inteligente. Doar o carte pe care o poți
              deschide peste 10 ani, la o cafea, într-o dimineață de
              duminică.
            </p>
          </div>
        </div>
      </section>

      {/* ─── Mosaic of moments ─── */}
      <section className="container-page py-4xl">
        <div className="mx-auto max-w-2xl text-center">
          <p className="kicker justify-center">Dovezi</p>
          <h2 className="mt-sm" style={{ fontVariationSettings: "'opsz' 144" }}>
            Nu promitem. Arătăm.
          </h2>
        </div>

        <div className="mt-2xl grid grid-cols-12 gap-md">
          <div className="col-span-6 sm:col-span-4 sm:col-start-2">
            <Polaroid
              src="https://images.unsplash.com/photo-1519741497674-611481863552?w=600&q=80"
              alt="Prima nuntă"
              caption="Eli & Marcu · '25"
              rotate={-2}
              aspect="4/5"
            />
          </div>
          <div className="col-span-6 sm:col-span-3 sm:mt-lg">
            <Polaroid
              src="https://images.unsplash.com/photo-1532712938310-34cb3982ef74?w=500&q=80"
              alt="Botez"
              caption="Botezul lui Teo"
              rotate={3}
              aspect="3/4"
            />
          </div>
          <div className="col-span-12 sm:col-span-4 sm:col-start-2">
            <Polaroid
              src="https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?w=700&q=80"
              alt="Corporate launch"
              caption="Christmas Party"
              rotate={1}
              aspect="3/2"
            />
          </div>
          <div className="col-span-12 sm:col-span-3 sm:-mt-md">
            <Polaroid
              src="https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=500&q=80"
              alt="Majorat"
              caption="Majorat, 18"
              rotate={-3}
              aspect="1/1"
            />
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="container-page pb-4xl">
        <div className="mx-auto max-w-2xl text-center">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-fg-soft">
            — sfârșitul manifestului
          </p>
          <h2 className="mt-sm font-serif" style={{ fontVariationSettings: "'opsz' 144" }}>
            Vrei să-ncerci?
          </h2>
          <p className="mt-md text-fg-soft">
            Nu costă nimic să te uiți. Și nici nu trebuie să iei o
            decizie acum. Doar uită-te la o galerie demo, pe telefon, în
            lumina dimineții.
          </p>
          <div className="mt-xl flex flex-wrap justify-center gap-sm">
            <a href="/#demo-live" className="btn-primary">
              Deschide galeria demo
            </a>
            <a href="mailto:guestlens.events@gmail.com" className="btn-ghost">
              Scrie-ne
            </a>
          </div>
        </div>
      </section>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}
